import { Alert, Button, Field, Modal } from '@catering/ui';
import { useMutation } from '@tanstack/react-query';
import { type JSX, useRef, useState } from 'react';

import {
  isDefiniteRejection,
  submitSignature,
  type SubmitResult,
} from '../../../deliveries/deliveries-repository';
import { signOnScreen, type DeliveryNote } from '../../../delivery-notes/api';
import { mapDeliveryNoteError } from '../../../delivery-notes/delivery-note-api-errors';
import { SignaturePad, type SignaturePadHandle } from './signature-pad';

const RECEIVER_DOCUMENT_PATTERN = /^\d{7,8}$/;

export interface SignOnScreenModalProps {
  note: DeliveryNote;
  onClose: () => void;
  onSigned: () => void;
  /**
   * Phone flow: the capture is stored on the device first and may stay queued.
   * Without it the modal sends straight to the server and shows any failure.
   */
  allowOffline?: boolean;
  /** The capture was stored but is not on the server yet (or the server refused it: see the card). */
  onQueued?: (result: Exclude<SubmitResult, { kind: 'synced' }>) => void;
}

/**
 * `delivery-note-documents/spec.md` → "Modal de firma en pantalla
 * accesible y mobile-first en back-office". Full-screen below `sm` via the
 * `Modal` `className` override (`design.md` §10.2) rather than a new
 * overlay component. Keyboard access (`Tab` canvas → "Borrar" →
 * "Confirmar firma", `Enter` on the enabled button) needs no extra code:
 * natural DOM order plus `Modal`'s existing focus trap already cover it.
 */
export function SignOnScreenModal({
  note,
  onClose,
  onSigned,
  allowOffline = false,
  onQueued,
}: SignOnScreenModalProps): JSX.Element {
  const padRef = useRef<SignaturePadHandle>(null);
  const [hasStroke, setHasStroke] = useState(false);
  const [receiverName, setReceiverName] = useState(note.defaultReceiverName ?? '');
  const [receiverDocument, setReceiverDocument] = useState('');
  // One capture per modal: a retry after a failure replays the same id and device time.
  const captureRef = useRef<{ clientCaptureId: string; clientSignedAt: string } | null>(null);

  const signMutation = useMutation({
    mutationFn: () => {
      const signatureDataUrl = padRef.current?.exportPng();

      if (signatureDataUrl === null || signatureDataUrl === undefined) {
        throw new Error('missing signature stroke');
      }

      captureRef.current ??= {
        clientCaptureId: crypto.randomUUID(),
        clientSignedAt: new Date().toISOString(),
      };

      const capture = { receiverName, receiverDocument, signatureDataUrl, ...captureRef.current };

      return allowOffline
        ? submitSignature(note.id, capture)
        : signOnScreen(note.id, capture).then((signed): SubmitResult => ({ kind: 'synced', note: signed }));
    },
    onSuccess: (result) => {
      if (result.kind === 'synced') {
        onSigned();
      } else {
        onQueued?.(result);
      }
    },
    onError: (error) => {
      // A network error or timeout may have reached the server: keep the id so a retry replays it.
      // A definite refusal never stored anything, so new strokes are a new capture.
      if (isDefiniteRejection(error)) {
        captureRef.current = null;
      }
    },
  });

  const canSubmit =
    hasStroke &&
    receiverName.trim() !== '' &&
    RECEIVER_DOCUMENT_PATTERN.test(receiverDocument) &&
    !signMutation.isPending;

  const title = note.status === 'DISPATCHED' ? 'Registrar entrega y firma' : 'Firmar en pantalla';

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      className="h-full max-h-full w-full rounded-none sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-lg sm:rounded-xl"
    >
      <div className="flex flex-col gap-4">
        {signMutation.isError ? (
          <Alert tone="danger">{mapDeliveryNoteError(signMutation.error)}</Alert>
        ) : null}

        <Field
          label="Nombre de quien recibe"
          name="receiverName"
          required
          value={receiverName}
          onChange={(event) => setReceiverName(event.target.value)}
        />
        <Field
          label="Documento"
          name="receiverDocument"
          required
          inputMode="numeric"
          hint="7 u 8 dígitos"
          value={receiverDocument}
          onChange={(event) => setReceiverDocument(event.target.value)}
        />

        <SignaturePad ref={padRef} onChange={setHasStroke} />

        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              padRef.current?.clear();
            }}
          >
            Borrar
          </Button>
          <Button
            loading={signMutation.isPending}
            disabled={!canSubmit}
            onClick={() => signMutation.mutate()}
          >
            Confirmar firma
          </Button>
        </div>
      </div>
    </Modal>
  );
}
